import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  ChevronDownIcon, 
  ChevronUpIcon,
  TrashIcon,
  FunnelIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  StopIcon
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useTaskStore, Task } from '../../stores/taskStore';
import { formatDistanceToNow } from 'date-fns';
import './TaskPanel.css';

interface TaskPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TaskPanel: React.FC<TaskPanelProps> = ({ isOpen, onClose }) => {
  const { tasks, activeTasks, removeTask, loadTasks, cancelTask } = useTaskStore();
  const [filter, setFilter] = useState<'all' | 'active' | 'completed' | 'failed'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'status'>('newest');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cancellingTasks, setCancellingTasks] = useState<Set<string>>(new Set());
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, onClose]);
  
  // Get all tasks as array
  const allTasks = Array.from(tasks.values());
  
  // Apply filters
  const filteredTasks = allTasks.filter(task => {
    switch (filter) {
      case 'active':
        return ['pending', 'processing', 'retrying'].includes(task.status);
      case 'completed':
        return task.status === 'completed';
      case 'failed':
        return task.status === 'failed';
      default:
        return true;
    }
  });
  
  // Apply sorting
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case 'oldest':
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      case 'status':
        const statusOrder: Record<string, number> = { 
          'processing': 0, 'pending': 1, 'retrying': 2, 'completed': 3, 'failed': 4, 'cancelled': 5 
        };
        return (statusOrder[a.status] || 6) - (statusOrder[b.status] || 6);
      default:
        return 0;
    }
  });
  
  const clearCompleted = () => {
    allTasks
      .filter(task => task.status === 'completed')
      .forEach(task => removeTask(task.id));
  };
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadTasks();
    } catch (error) {
      console.error('Failed to refresh tasks:', error);
    } finally {
      setIsRefreshing(false);
    }
  };
  
  const exportTasks = () => {
    const getTaskDurationForExport = (task: Task) => {
      if (task.completed_at) {
        const start = new Date(task.started_at || task.created_at);
        const end = new Date(task.completed_at);
        const duration = Math.round((end.getTime() - start.getTime()) / 1000);
        
        if (duration < 60) return `${duration}s`;
        if (duration < 3600) return `${Math.floor(duration / 60)}m ${duration % 60}s`;
        return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
      }
      
      if (task.started_at) {
        return formatDistanceToNow(new Date(task.started_at), { addSuffix: false });
      }
      
      return 'Not started';
    };
    
    const exportData = {
      exported_at: new Date().toISOString(),
      total_tasks: allTasks.length,
      active_tasks: activeTasks.length,
      tasks: allTasks.map(task => ({
        ...task,
        duration: getTaskDurationForExport(task)
      }))
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `task-history-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className={clsx(
      "fixed bottom-0 right-0 w-full max-w-2xl h-[600px] bg-white rounded-tl-xl shadow-2xl border-l border-t border-gray-200 z-50 flex flex-col transition-all duration-300 ease-out",
      isOpen ? "task-panel-enter-active" : "task-panel-exit-active"
    )}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">
            Task Manager
          </h3>
          <div className="flex items-center gap-3 text-sm text-gray-600">
            {activeTasks.length > 0 && (
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                {activeTasks.length} active
              </span>
            )}
            {allTasks.length > activeTasks.length && (
              <span className="flex items-center gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                {allTasks.length - activeTasks.length} completed
              </span>
            )}
            {allTasks.length === 0 && (
              <span className="text-gray-400">No tasks</span>
            )}
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={clsx(
              "flex items-center gap-1 px-2 py-1 rounded-md transition-colors text-sm",
              isRefreshing 
                ? "text-gray-400 cursor-not-allowed" 
                : "text-gray-600 hover:bg-gray-100"
            )}
            title="Refresh tasks"
          >
            <ArrowPathIcon className={clsx(
              "w-4 h-4",
              isRefreshing && "animate-spin"
            )} />
            Refresh
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronDownIcon className="w-5 h-5 text-gray-500" />
          </button>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>
      
      {/* Filters and Actions */}
      <div className="flex items-center justify-between gap-3 p-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-4 h-4 text-gray-500" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Tasks</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="status">Status</option>
            </select>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={exportTasks}
            disabled={allTasks.length === 0}
            className={clsx(
              "flex items-center gap-1 text-sm px-3 py-1 rounded-md transition-colors",
              allTasks.length > 0
                ? "text-blue-600 hover:bg-blue-50"
                : "text-gray-400 cursor-not-allowed"
            )}
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Export
          </button>
          
          <button
            onClick={clearCompleted}
            disabled={!allTasks.some(t => t.status === 'completed')}
            className={clsx(
              "flex items-center gap-1 text-sm px-3 py-1 rounded-md transition-colors",
              allTasks.some(t => t.status === 'completed')
                ? "text-red-600 hover:bg-red-50"
                : "text-gray-400 cursor-not-allowed"
            )}
          >
            <TrashIcon className="w-4 h-4" />
            Clear Completed
          </button>
        </div>
      </div>
      
      {/* Task List */}
      <div className="flex-1 overflow-y-auto p-2">
        {sortedTasks.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No tasks found</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            {sortedTasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                isCancelling={cancellingTasks.has(task.id)}
                onCancel={async () => {
                  setCancellingTasks(prev => new Set(Array.from(prev).concat(task.id)));
                  try {
                    await cancelTask(task.id);
                  } catch (error) {
                    console.error('Failed to cancel task:', error);
                  } finally {
                    setCancellingTasks(prev => {
                      const newSet = new Set(Array.from(prev));
                      newSet.delete(task.id);
                      return newSet;
                    });
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface TaskCardProps {
  task: Task;
  isCancelling?: boolean;
  onCancel?: () => Promise<void>;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, isCancelling = false, onCancel }) => {
  const { removeTask } = useTaskStore();
  const [expanded, setExpanded] = useState(false);
  
  const getTaskDuration = (task: Task) => {
    if (task.completed_at) {
      const start = new Date(task.started_at || task.created_at);
      const end = new Date(task.completed_at);
      const duration = Math.round((end.getTime() - start.getTime()) / 1000);
      
      if (duration < 60) return `${duration}s`;
      if (duration < 3600) return `${Math.floor(duration / 60)}m ${duration % 60}s`;
      return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
    }
    
    if (task.started_at) {
      return formatDistanceToNow(new Date(task.started_at), { addSuffix: false });
    }
    
    return 'Not started';
  };

  const getTaskTypeLabel = () => {
    switch (task.task_type) {
      case 'image_fetch_single':
        return 'Fetching images for 1 property';
      case 'image_fetch_bulk':
        return `Fetching images for ${task.progress.total} properties`;
      case 'image_fetch_all':
        return 'Fetching images for all properties';
      default:
        return task.task_type.replace(/_/g, ' ');
    }
  };
  
  const getRowStyle = () => {
    switch (task.status) {
      case 'completed':
        return 'border-l-green-400 bg-green-50/40 hover:bg-green-50';
      case 'failed':
        return 'border-l-red-400 bg-red-50/50 hover:bg-red-50';
      case 'processing':
        return 'border-l-blue-400 bg-blue-50/40 hover:bg-blue-50';
      case 'retrying':
        return 'border-l-yellow-400 bg-yellow-50/50 hover:bg-yellow-50';
      default:
        return 'border-l-gray-300 bg-white hover:bg-gray-50';
    }
  };

  const getStatusPillStyle = () => {
    switch (task.status) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'failed':
        return 'bg-red-100 text-red-700';
      case 'processing':
        return 'bg-blue-100 text-blue-700';
      case 'retrying':
        return 'bg-yellow-100 text-yellow-700';
      case 'cancelled':
        return 'bg-gray-200 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getProgressBarStyle = () => {
    switch (task.status) {
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      case 'retrying':
        return 'bg-yellow-500';
      default:
        return 'bg-blue-500';
    }
  };
  
  const canDismiss = ['completed', 'failed', 'cancelled'].includes(task.status);
  const canCancel = ['pending', 'processing', 'retrying'].includes(task.status) && onCancel;
  const isActive = ['pending', 'processing', 'retrying'].includes(task.status);
  const hasProgress = task.progress.total > 0;
  const progressPercent = Math.max(0, Math.min(100, Math.round(task.progress.percentage || 0)));
  const taskLabel = getTaskTypeLabel();
  const shortTaskId = task.id.slice(0, 8);
  
  return (
    <div className={clsx(
      "border-b border-l-4 border-gray-100 px-3 py-1.5 transition-colors last:border-b-0",
      getRowStyle()
    )}>
      <div className="flex items-center gap-2">
        <div className="shrink-0">
          {getStatusIcon(task.status)}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <h4 className="min-w-0 flex-1 truncate text-xs font-medium text-gray-900" title={taskLabel}>
              {taskLabel}
            </h4>
            <span className={clsx("shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-medium", getStatusPillStyle())}>
              {task.status}
            </span>
            {isActive && (
              <div
                className="h-1.5 w-20 shrink-0 rounded-full bg-gray-200 sm:w-28 md:w-36"
                title={hasProgress ? `${task.progress.current} of ${task.progress.total} (${progressPercent}%)` : 'Progress not reported'}
              >
                <div
                  className={clsx("h-1.5 rounded-full progress-bar-animated", getProgressBarStyle())}
                  style={{ width: `${hasProgress ? progressPercent : 0}%` }}
                />
              </div>
            )}
            <span className="w-14 shrink-0 text-right text-xs tabular-nums text-gray-600" title={`Duration: ${getTaskDuration(task)}`}>
              {getTaskDuration(task)}
            </span>
            <span className="w-16 shrink-0 text-right text-[11px] tabular-nums text-gray-500" title={task.id}>
              {shortTaskId}
            </span>
          </div>

          {/* Expandable Details */}
          {expanded && (
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 border-t border-gray-200 pt-2 text-xs text-gray-600">
              <div><strong>Task Type:</strong> {taskLabel}</div>
              <div><strong>Task ID:</strong> {task.id}</div>
              <div><strong>Created:</strong> {new Date(task.created_at).toLocaleString()}</div>
              {task.started_at && (
                <div><strong>Started:</strong> {new Date(task.started_at).toLocaleString()}</div>
              )}
              {task.completed_at && (
                <div><strong>Completed:</strong> {new Date(task.completed_at).toLocaleString()}</div>
              )}
              <div><strong>Priority:</strong> {task.priority}</div>
              <div><strong>Duration:</strong> {getTaskDuration(task)}</div>
              {hasProgress && (
                <div><strong>Progress:</strong> {task.progress.current} of {task.progress.total} ({progressPercent}%)</div>
              )}
              {task.retry_count > 0 && (
                <div><strong>Retries:</strong> {task.retry_count}</div>
              )}
              {task.progress.message && (
                <div className="col-span-2"><strong>Message:</strong> {task.progress.message}</div>
              )}
              {task.error_message && (
                <div className="col-span-2 text-red-700"><strong>Error:</strong> {task.error_message}</div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex shrink-0 items-center gap-1">
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-white/80 rounded transition-colors"
            title={expanded ? "Hide task details" : "Show task details"}
            aria-label={expanded ? "Hide task details" : "Show task details"}
          >
            {expanded ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-500" />
            )}
          </button>
          
          {canCancel && (
            <button
              onClick={onCancel}
              disabled={isCancelling}
              className={clsx(
                "p-1 rounded transition-colors",
                isCancelling 
                  ? "text-gray-400 cursor-not-allowed" 
                  : "text-orange-500 hover:bg-white/80"
              )}
              title="Cancel task"
              aria-label="Cancel task"
            >
              <StopIcon className={clsx(
                "w-4 h-4",
                isCancelling && "animate-spin"
              )} />
            </button>
          )}
          
          {canDismiss && (
            <button
              onClick={() => removeTask(task.id)}
              className="p-1 hover:bg-white/80 rounded transition-colors"
              title="Remove from list"
              aria-label="Remove from list"
            >
              <XMarkIcon className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to get task status icon
const getStatusIcon = (status: Task['status']) => {
  switch (status) {
    case 'completed':
      return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
    case 'failed':
      return <XCircleIcon className="w-4 h-4 text-red-500" />;
    case 'processing':
      return <ArrowPathIcon className="w-4 h-4 text-blue-500 animate-spin" />;
    case 'retrying':
      return <ArrowPathIcon className="w-4 h-4 text-yellow-500 animate-spin" />;
    default:
      return <ClockIcon className="w-4 h-4 text-gray-500" />;
  }
};
